import React from 'react';
interface InvoiceHeaderProps {
  company: {
    name: string;
    address: string[];
    uen: string;
    gst: string;
    travelLicense?: string;
    logoUrl?: string;
  };
  invoice: {
    number: string;
    issueDate: string;
    bookingRef: string;
    dueDate: string;
    salesStaff: string;
    bookingStaff: string;
    productName: string;
    printedDate: string;
    lastUpdateDate: string;
  };
  billTo: {
    name: string;
    address: string[];
    attn?: string;
  };
  pageNumber?: number;
  totalPages?: number;
  isFirstPage?: boolean;
}
export function InvoiceHeader({
  company,
  invoice,
  billTo,
  pageNumber = 1,
  totalPages = 1,
  isFirstPage = true
}: InvoiceHeaderProps) {
  // Full header shown on all pages now
  return <>
      {/* Top Section: Company Info + Invoice Metadata */}
      <div className="flex justify-between items-start mb-8" data-id="element-505">
        {/* Left: Company Info - Subtle and Supporting */}
        <div className="flex-1" data-id="element-506">
          {company.logoUrl ? <img src={company.logoUrl} alt={company.name} className="h-16 mb-4 object-contain object-left" data-id="element-507" /> : <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg mb-4 flex items-center justify-center shadow-md" data-id="element-508">
              <span className="text-white font-bold text-xl" data-id="element-509">PA</span>
            </div>}
          <h1 className="text-base font-bold text-neutral-900 mb-1.5" data-id="element-510">
            {company.name}
          </h1>
          <div className="text-xs text-neutral-500 space-y-0.5 leading-relaxed" data-id="element-511">
            {company.address.map((line, i) => <p key={i} data-id="element-512">{line}</p>)}
            <p className="mt-1.5" data-id="element-513">UEN: {company.uen}</p>
            <p data-id="element-514">GST: {company.gst}</p>
            {company.travelLicense && <p data-id="element-515">Travel Agent License: {company.travelLicense}</p>}
          </div>
        </div>

        {/* Right: Invoice Info Box */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5 min-w-[360px] shadow-sm" data-id="element-516">
          <h2 className="text-lg font-bold text-neutral-900 mb-4 tracking-tight" data-id="element-517">
            TAX INVOICE
          </h2>
          <div className="space-y-2" data-id="element-518">
            <div className="flex justify-between" data-id="element-519">
              <span className="text-xs font-medium text-neutral-600" data-id="element-520">
                Invoice No.:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-521">
                {invoice.number}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-522">
              <span className="text-xs font-medium text-neutral-600" data-id="element-523">
                Issue Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-524">
                {invoice.issueDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-525">
              <span className="text-xs font-medium text-neutral-600" data-id="element-526">
                Booking Ref:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-527">
                {invoice.bookingRef}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-528">
              <span className="text-xs font-medium text-neutral-600" data-id="element-529">
                Due Date:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-530">
                {invoice.dueDate}
              </span>
            </div>
            <div className="h-px bg-neutral-200 my-2" data-id="element-531" />
            <div className="flex justify-between" data-id="element-532">
              <span className="text-xs font-medium text-neutral-600" data-id="element-533">
                Sales Staff:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-534">
                {invoice.salesStaff}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-535">
              <span className="text-xs font-medium text-neutral-600" data-id="element-536">
                Booking Staff:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-537">
                {invoice.bookingStaff}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-538">
              <span className="text-xs font-medium text-neutral-600" data-id="element-539">
                Product Name:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-540">
                {invoice.productName}
              </span>
            </div>
            <div className="h-px bg-neutral-200 my-2" data-id="element-541" />
            <div className="flex justify-between" data-id="element-542">
              <span className="text-xs font-medium text-neutral-600" data-id="element-543">
                Printed Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-544">
                {invoice.printedDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-545">
              <span className="text-xs font-medium text-neutral-600" data-id="element-546">
                Last Update:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-547">
                {invoice.lastUpdateDate}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To Section - Only on first page */}
      {isFirstPage && <div className="mb-8 bg-neutral-50 border border-neutral-200 rounded-lg p-5" data-id="element-548">
          <p className="text-sm font-bold text-neutral-900 mb-3 uppercase tracking-wide" data-id="element-549">
            Bill To:
          </p>
          <div className="space-y-1" data-id="element-550">
            <p className="text-base font-semibold text-neutral-900" data-id="element-551">
              {billTo.name}
            </p>
            <div className="text-sm text-neutral-700 space-y-0.5" data-id="element-552">
              {billTo.address.map((line, i) => <p key={i} data-id="element-553">{line}</p>)}
            </div>
            {billTo.attn && <p className="text-sm text-neutral-700 mt-2" data-id="element-554">
                Attn: {billTo.attn}
              </p>}
          </div>
        </div>}
    </>;
}